import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Shield, CheckCircle2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { heroImages, contactInfo } from '../data/mock';
import { track } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const trustPoints = [
  { icon: Clock, text: 'Confirmation within minutes' },
  { icon: Shield, text: 'Professional, vetted chauffeurs' },
  { icon: CheckCircle2, text: 'No obligation — free quote' },
  { icon: Phone, text: 'Direct: +1 (437) 996-3801' },
];

export const Contact = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', pickupLocation: '',
    dropoffLocation: '', dateTime: '', notes: '',
  });

  // Prefill from /contact?pickup=…&dropoff=…&date=…&vehicle=…
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pickup = params.get('pickup') || '';
    const dropoff = params.get('dropoff') || '';
    const date = params.get('date') || '';
    const vehicle = params.get('vehicle') || '';
    if (pickup || dropoff || date || vehicle) {
      setFormData((prev) => ({
        ...prev,
        pickupLocation: pickup || prev.pickupLocation,
        dropoffLocation: dropoff || prev.dropoffLocation,
        dateTime: date ? `${date}T09:00` : prev.dateTime,
        notes: vehicle ? `Preferred vehicle: ${vehicle}${prev.notes ? `\n\n${prev.notes}` : ''}` : prev.notes,
      }));
      // Smooth scroll to the booking form anchor
      setTimeout(() => {
        const el = document.getElementById('booking-form');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('fade-in'); }),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.fade-element').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        track('booking_submit_success', { flow: 'contact_form', booking_id: data.booking_id });
        setBookingId(data.booking_id);
        setSubmitted(true);
        toast({ title: `Booking ${data.booking_id} Received`, description: data.message });
      } else {
        track('booking_submit_error', { flow: 'contact_form', status: res.status });
        toast({ title: "Error", description: "Something went wrong. Please try again or call us directly.", variant: "destructive" });
      }
    } catch {
      track('booking_submit_error', { flow: 'contact_form', status: 'network' });
      toast({ title: "Error", description: "Unable to submit. Please call us directly.", variant: "destructive" });
    }
    setSubmitting(false);
  };

  const inputCls = "bg-[rgba(22,22,22,0.8)] border-[rgba(194,168,120,0.12)] text-[#F7F3EC] placeholder:text-[rgba(200,200,200,0.25)] focus:border-[rgba(194,168,120,0.4)] focus:bg-[rgba(28,28,28,0.85)] font-light text-sm h-[52px] transition-all duration-300";
  const labelSt = { display: 'block', fontSize: '10px', color: 'rgba(194,168,120,0.6)', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 400, marginBottom: '10px' };

  const SectionHeader = ({ num, title }) => (
    <div className="flex items-center" style={{ gap: '16px', marginBottom: '32px' }}>
      <span style={{ fontSize: '12px', color: 'rgba(194,168,120,0.45)', letterSpacing: '2px', fontWeight: 400 }}>{num}</span>
      <div style={{ width: '24px', height: '1px', backgroundColor: 'rgba(194,168,120,0.2)' }} />
      <span style={{ fontSize: '11px', color: 'rgba(220,220,220,0.6)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 400 }}>{title}</span>
    </div>
  );

  return (
    <div data-testid="contact-page" style={{ backgroundColor: '#0A0A0A' }}>

      {/* ── HERO ── */}
      <section data-testid="contact-hero" className="relative overflow-hidden flex items-center justify-center" style={{ minHeight: '75vh' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${heroImages.contact})`,
          filter: 'brightness(0.42) saturate(0.85) contrast(1.08)',
          scale: '1.05',
          animation: 'heroZoom 25s ease-in-out infinite alternate',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.18) 35%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.75) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 40%, rgba(194,168,120,0.05) 0%, transparent 55%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ paddingTop: '160px', paddingBottom: '140px' }}>
          <div className="fade-element" style={{ animationDelay: '0.2s' }}>
            <div className="gold-divider mx-auto" style={{ marginBottom: '28px', opacity: 0.7 }} />
            <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.5)', letterSpacing: '5px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '32px' }}>
              Get in Touch
            </p>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.4s' }}>
            <h1 data-testid="contact-hero-heading" className="font-serif" style={{
              fontSize: 'clamp(38px, 5.5vw, 64px)', fontWeight: 500, color: '#FAFAFA',
              marginBottom: '28px', lineHeight: 1.08, textShadow: '0 2px 40px rgba(0,0,0,0.5)',
            }}>
              We're Here to Serve You
            </h1>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.6s' }}>
            <p style={{ fontSize: '17px', color: 'rgba(220,220,220,0.6)', fontWeight: 300, lineHeight: 1.8, maxWidth: '500px', margin: '0 auto' }}>
              Whether you're planning ahead or need a last-minute ride, we're ready to help.
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0" style={{ height: '180px', background: 'linear-gradient(to top, #0A0A0A, transparent)' }} />
      </section>

      {/* ── FORM SECTION ── */}
      <section id="booking-form" className="relative overflow-hidden" style={{ backgroundColor: '#0A0A0A', padding: '4rem 1.5rem 10rem' }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 40% 20%, rgba(194,168,120,0.025) 0%, transparent 50%)',
        }} />
        <div className="absolute top-0 left-0 right-0" style={{
          height: '400px',
          backgroundImage: `url(${heroImages.contact})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          filter: 'brightness(0.12) saturate(0.45)',
          maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 100%)',
        }} />

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12" style={{ gap: '56px' }}>

            {/* ── MAIN FORM ── */}
            <div className="lg:col-span-8">
              {submitted ? (
                <div className="fade-element" style={{
                  padding: '80px 56px', textAlign: 'center',
                  backgroundColor: 'rgba(14,14,14,0.7)',
                  backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(194,168,120,0.1)',
                  boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
                }}>
                  <div style={{
                    width: '64px', height: '64px', margin: '0 auto 32px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'rgba(194,168,120,0.06)', border: '1px solid rgba(194,168,120,0.15)',
                    borderRadius: '50%',
                  }}>
                    <CheckCircle2 size={28} style={{ color: '#C2A878', opacity: 0.8 }} />
                  </div>
                  <h2 className="font-serif" style={{ fontSize: '32px', color: '#F5F5F5', marginBottom: '16px' }}>
                    Booking #{bookingId} Received
                  </h2>
                  <p style={{ fontSize: '15px', color: 'rgba(204,204,204,0.55)', fontWeight: 300, lineHeight: 1.8, maxWidth: '420px', margin: '0 auto 36px' }}>
                    Thank you for reaching out. We'll review your request and get back to you shortly.
                  </p>
                  <button
                    data-testid="book-another-btn"
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: '', email: '', phone: '', pickupLocation: '', dropoffLocation: '', dateTime: '', notes: '' });
                    }}
                    style={{
                      backgroundColor: 'transparent', color: '#C2A878', padding: '13px 28px',
                      fontSize: '12px', fontWeight: 400, letterSpacing: '1px', textTransform: 'uppercase',
                      border: '1px solid rgba(194,168,120,0.25)', cursor: 'pointer', transition: 'all 400ms ease',
                    }}
                    onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgba(194,168,120,0.06)'; e.target.style.borderColor = 'rgba(194,168,120,0.45)'; }}
                    onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = 'rgba(194,168,120,0.25)'; }}
                  >
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <div className="fade-element" style={{
                  backgroundColor: 'rgba(14,14,14,0.7)',
                  backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(194,168,120,0.08)',
                  boxShadow: '0 24px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(194,168,120,0.04)',
                  padding: 'clamp(36px, 5vw, 64px)',
                  position: 'relative',
                }}>
                  <div style={{
                    position: 'absolute', top: 0, left: '15%', right: '15%', height: '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.15), transparent)',
                  }} />

                  <form onSubmit={handleSubmit} data-testid="booking-form">

                    {/* SECTION 1: Your Details */}
                    <SectionHeader num="01" title="Your Details" />
                    <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: '20px', marginBottom: '20px' }}>
                      <div>
                        <label style={labelSt}>Full Name</label>
                        <Input type="text" name="name" value={formData.name} onChange={handleChange} required data-testid="input-name" className={inputCls} placeholder="Your full name" />
                      </div>
                      <div>
                        <label style={labelSt}>Email Address</label>
                        <Input type="email" name="email" value={formData.email} onChange={handleChange} required data-testid="input-email" className={inputCls} placeholder="your@email.com" />
                      </div>
                    </div>
                    <div style={{ marginBottom: '48px' }}>
                      <label style={labelSt}>Phone Number</label>
                      <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} required data-testid="input-phone" className={inputCls} placeholder="+1 (416) 555-0123" />
                    </div>

                    <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.08), transparent)', marginBottom: '48px' }} />

                    {/* SECTION 2: Journey Details */}
                    <SectionHeader num="02" title="Journey Details" />
                    <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: '20px', marginBottom: '20px' }}>
                      <div>
                        <label style={labelSt}>Pickup Location</label>
                        <Input type="text" name="pickupLocation" value={formData.pickupLocation} onChange={handleChange} required data-testid="input-pickup" className={inputCls} placeholder="Your pickup address" />
                      </div>
                      <div>
                        <label style={labelSt}>Drop-off Location</label>
                        <Input type="text" name="dropoffLocation" value={formData.dropoffLocation} onChange={handleChange} required data-testid="input-dropoff" className={inputCls} placeholder="Pearson Airport, Terminal 1" />
                      </div>
                    </div>
                    <div style={{ marginBottom: '48px' }}>
                      <label style={labelSt}>Date & Time</label>
                      <Input type="datetime-local" name="dateTime" value={formData.dateTime} onChange={handleChange} required data-testid="input-datetime" className={inputCls} />
                    </div>

                    <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.08), transparent)', marginBottom: '48px' }} />

                    {/* SECTION 3: Additional Info */}
                    <SectionHeader num="03" title="Additional Information" />
                    <div style={{ marginBottom: '48px' }}>
                      <label style={labelSt}>Notes / Special Requests</label>
                      <Textarea
                        name="notes" value={formData.notes} onChange={handleChange} rows={4}
                        data-testid="input-notes"
                        className="bg-[rgba(22,22,22,0.8)] border-[rgba(194,168,120,0.12)] text-[#F7F3EC] placeholder:text-[rgba(200,200,200,0.25)] focus:border-[rgba(194,168,120,0.4)] focus:bg-[rgba(28,28,28,0.85)] font-light text-sm transition-all duration-300"
                        placeholder="Number of passengers, luggage, child seats, special timing..."
                      />
                    </div>

                    <button
                      type="submit" disabled={submitting}
                      data-testid="submit-booking-btn"
                      style={{
                        width: '100%', padding: '16px', fontSize: '12px', fontWeight: 500,
                        letterSpacing: '1.2px', textTransform: 'uppercase',
                        backgroundColor: submitting ? '#9e8a66' : '#C2A878', color: '#0A0A0A',
                        border: 'none', cursor: submitting ? 'wait' : 'pointer',
                        transition: 'all 400ms ease',
                        boxShadow: '0 4px 24px rgba(194,168,120,0.12)',
                      }}
                      onMouseEnter={(e) => { if (!submitting) { e.target.style.backgroundColor = '#D4BA8A'; e.target.style.boxShadow = '0 6px 32px rgba(194,168,120,0.22)'; } }}
                      onMouseLeave={(e) => { if (!submitting) { e.target.style.backgroundColor = '#C2A878'; e.target.style.boxShadow = '0 4px 24px rgba(194,168,120,0.12)'; } }}
                    >
                      {submitting ? 'Submitting Your Request...' : 'Submit Booking Request'}
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* ── SIDE PANEL ── */}
            <div className="lg:col-span-4">
              <div className="lg:sticky" style={{ top: '140px' }}>

                {/* Trust Card */}
                <div className="fade-element" style={{
                  padding: '40px 32px',
                  backgroundColor: 'rgba(14,14,14,0.5)',
                  backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(194,168,120,0.06)',
                  marginBottom: '32px',
                }}>
                  <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.55)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 400, marginBottom: '28px' }}>
                    What to Expect
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {trustPoints.map((point) => (
                      <div key={point.text} className="flex items-start" style={{ gap: '14px' }}>
                        <div style={{
                          width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: 'rgba(194,168,120,0.04)', border: '1px solid rgba(194,168,120,0.08)',
                          flexShrink: 0,
                        }}>
                          <point.icon size={15} strokeWidth={1.5} style={{ color: '#C2A878', opacity: 0.6 }} />
                        </div>
                        <span style={{ fontSize: '13px', color: 'rgba(225,225,225,0.85)', fontWeight: 300, lineHeight: 1.6, paddingTop: '8px' }}>
                          {point.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact Info Card */}
                <div className="fade-element" style={{
                  padding: '40px 32px',
                  backgroundColor: 'rgba(14,14,14,0.5)',
                  backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(194,168,120,0.06)',
                  marginBottom: '32px',
                }}>
                  <p data-testid="contact-info-heading" style={{ fontSize: '10px', color: 'rgba(194,168,120,0.55)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 400, marginBottom: '28px' }}>
                    Contact Information
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {[
                      { icon: Phone, label: 'Phone', value: contactInfo.phone, href: `tel:${contactInfo.phone.replace(/[^+\d]/g, '')}` },
                      { icon: Mail, label: 'Email', value: contactInfo.email, href: `mailto:${contactInfo.email}` },
                      { icon: MapPin, label: 'Area', value: contactInfo.serviceArea, href: 'https://maps.google.com/?q=Greater+Toronto+Area', ext: true },
                      { icon: Clock, label: 'Hours', value: contactInfo.hours },
                    ].map((item) => (
                      <div key={item.label} className="flex items-start" style={{ gap: '14px' }}>
                        <div style={{
                          width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: 'rgba(194,168,120,0.04)', border: '1px solid rgba(194,168,120,0.08)',
                          flexShrink: 0,
                        }}>
                          <item.icon size={15} strokeWidth={1.5} style={{ color: '#C2A878', opacity: 0.6 }} />
                        </div>
                        <div style={{ paddingTop: '4px' }}>
                          <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.45)', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '4px' }}>{item.label}</p>
                          {item.href ? (
                            <a href={item.href} data-testid={`contact-info-${item.label.toLowerCase()}`}
                              {...(item.ext ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                              style={{ fontSize: '14px', color: 'rgba(245,245,245,0.75)', fontWeight: 300, textDecoration: 'none', transition: 'color 180ms ease', display: 'block' }}
                              onMouseEnter={(e) => { e.target.style.color = '#C2A878'; }}
                              onMouseLeave={(e) => { e.target.style.color = 'rgba(245,245,245,0.75)'; }}
                            >{item.value}</a>
                          ) : (
                            <p style={{ fontSize: '14px', color: 'rgba(245,245,245,0.75)', fontWeight: 300 }}>{item.value}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Urgent CTA */}
                <div className="fade-element" style={{
                  padding: '28px 32px',
                  backgroundColor: 'rgba(194,168,120,0.03)',
                  border: '1px solid rgba(194,168,120,0.06)',
                }}>
                  <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.5)', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 400, marginBottom: '12px' }}>
                    Need immediate assistance?
                  </p>
                  <a
                    href={`tel:${contactInfo.phone.replace(/[^+\d]/g, '')}`}
                    data-testid="call-now-btn"
                    style={{
                      display: 'block', textAlign: 'center', padding: '13px',
                      fontSize: '12px', fontWeight: 400, letterSpacing: '1px', textTransform: 'uppercase',
                      border: '1px solid rgba(194,168,120,0.2)', color: '#C2A878',
                      textDecoration: 'none', transition: 'all 400ms ease',
                    }}
                    onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgba(194,168,120,0.06)'; e.target.style.borderColor = 'rgba(194,168,120,0.45)'; }}
                    onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = 'rgba(194,168,120,0.2)'; }}
                  >
                    Call Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT HAPPENS NEXT ── */}
      <section data-testid="next-steps" className="relative overflow-hidden" style={{ backgroundColor: '#0C0C0C', padding: '10rem 1.5rem' }}>
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.03 }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(194,168,120,0.03) 0%, transparent 50%)',
        }} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="fade-element" style={{ marginBottom: '72px' }}>
            <div className="gold-divider mx-auto" style={{ marginBottom: '28px', opacity: 0.6 }} />
            <h2 className="font-serif" style={{ fontSize: 'clamp(28px, 3.5vw, 42px)', fontWeight: 500, color: '#FAFAFA', textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
              What Happens Next?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: '56px' }}>
            {[
              { num: '01', title: 'We Receive Your Request', desc: 'Your booking request is reviewed personally by our team.' },
              { num: '02', title: 'Instant Confirmation', desc: "You'll receive an SMS confirmation with your booking details." },
              { num: '03', title: "You're All Set", desc: "Relax \u2014 we'll be there exactly when you need us." },
            ].map((step, i) => (
              <div key={i} className="fade-element">
                <p style={{ fontSize: '12px', color: 'rgba(194,168,120,0.4)', letterSpacing: '3px', fontWeight: 300, marginBottom: '20px' }}>{step.num}</p>
                <div style={{ width: '1px', height: '20px', backgroundColor: 'rgba(194,168,120,0.15)', margin: '0 auto 20px' }} />
                <h3 className="font-serif" style={{ fontSize: '18px', fontWeight: 400, color: '#F5F5F5', marginBottom: '12px' }}>{step.title}</h3>
                <p style={{ fontSize: '14px', color: 'rgba(225,225,225,0.8)', fontWeight: 300, lineHeight: 1.7 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @keyframes heroZoom {
          0% { transform: scale(1.05); }
          100% { transform: scale(1.12); }
        }
      `}</style>
    </div>
  );
};
